import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { Heading, Pill } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { useApolloClient } from "@apollo/client";
import { Step } from "react-joyride";
import { groupSection } from "../graphql/groupSection.query";
import { BaseQuery, CubeQuery, Group } from "@models/ISchema";
import { GroupContext } from "../context/GroupContext";
import { Types } from "../context/groupInfo.reducer";
import { serializeSections } from "@services/collections/sections/sectionsAdapters";
import { ISectionData } from "../../../sections/ISections";
import { useTranslation } from "react-i18next";
import {
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
} from "@context/useOnboardingStore";
import { ContextApp } from "@config/Context/contextApp";
import { ContextEDH } from "@context/ContextEDH";
import { Events, useListenEvent } from "@common/hooks/useListenEvent";
import LinkRouter from "@common/components/link-router";
import SystemLabel from "@common/components/system-label";
import TourTooltip from "@common/components/tour-tooltip";
import useContextUrl from "@common/hooks/useContextUrl";
import {
  getProgrammingPillLabelKey,
  isProgrammingLabelCode,
  resolveProgrammingGroupState,
} from "@utils/programmingGroupState";
import Tags from "../GridSections/Tags/Tags";
import Restrictions from "../GridSections/restrictions";
import Reference from "../GridSections/Reference/Reference";
import ProposalsAccess from "../components/proposals-access";
import styles from "../../../../common/components/Header/header.module.scss";
import groupStyles from "../GridSections/group.module.scss";

const attributeTag = "itesm.mx/attribute";

interface UseGetHeaderInfoProps {
  externalId?: string | number;
  onShowTagsDrawer?: () => void;
  onShowRestrictionsDrawer?: () => void;
  onShowReferenceDrawer?: () => void;
  /**
   * "Ver solicitudes de cambio": passed only for a user who can have assignment proposals.
   * `hasIndicator` turns on the dot that tells a reviewer they have proposals to review.
   */
  proposalsAccess?: { hasIndicator: boolean; onOpen: () => void } | null;
}

const useGetHeaderInfo = (props?: UseGetHeaderInfoProps) => {
  const {
    externalId,
    onShowTagsDrawer,
    onShowRestrictionsDrawer,
    onShowReferenceDrawer,
    proposalsAccess,
  } = props ?? {};
  const client = useApolloClient();
  const context = useContext(ContextApp);
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { id, origin, scenario, workspace } = useContextUrl();
  const { state, dispatch } = useContext(GroupContext);
  const { state: baseState } = useContext(ContextEDH);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [calendarData, setCalendar] = useState(null);
  const onboardingsSeen = useOnboardingStore(state => state.onboardingsSeen);

  const showHotspot = useMemo(() => {
    return shouldShowHotspot("system-labels");
  }, [onboardingsSeen]);

  const steps: Step[] = [
    {
      content: t("onboarding.system-labels.description"),
      target: ".systemLabelTour",
      locale: {
        last: t("onboarding.actions.done"),
      },
      title: t("onboarding.system-labels.title"),
      disableBeacon: false,
      placement: "right",
      placementBeacon: "right",
    },
  ];

  const variables = {
    query: groupSection,
    variables: {
      resourceId: externalId || id,
      scenarioId: scenario,
      originId: origin,
      resourceType: "GROUP",
    },
  };

  const getDataSections = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.query(variables);
      if (data) {
        const cubeQuery: CubeQuery = data?.cube;
        const baseQuery: BaseQuery = data?.base;
        const customFieldsQuery = baseQuery?.customFields;
        const info: any = cubeQuery.editorView.info;
        const groupInfo: Group = info.group;
        if (groupInfo) {
          dispatch({
            type: Types.SetGroupInfo,
            payload: groupInfo,
          });
          dispatch({
            type: Types.SetGroupCustomFields,
            payload: customFieldsQuery,
          });
          const sections: ISectionData = serializeSections(data);
          setCalendar(sections);
        } else {
          setError(true);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setError(true);
      setLoading(false);
    }
  }, [client, variables]);

  const getHeaderInfo = (groupInfo: Group, customLabel?: any) => {
    const details = [];
    const uniqueRow = [];
    const systemLabels = [];
    const courseTags = JSON.parse(groupInfo?.course?.tags || "{}");

    if (groupInfo.type) {
      details.push({
        title: t("dimension.group.header.more-details.type"),
        content: groupInfo?.type || "",
      });
    }

    if (groupInfo.totalBlocks) {
      details.push({
        title: t("dimension.group.header.more-details.total-blocks-group"),
        content: groupInfo?.totalBlocks?.toString() || "",
      });
    }

    if (groupInfo.totalBlocksWithInstructors) {
      details.push({
        title: t("dimension.group.header.more-details.blocks-with-total-teachers-group"),
        content: groupInfo?.totalBlocksWithInstructors?.toString() || "",
      });
    }

    if (attributeTag in courseTags) {
      details.push({
        title: t("dimension.group.header.more-details.attributes"),
        content: courseTags?.[attributeTag] || "-",
        className: "withBreakLines",
      });
    }

    if (customLabel) {
      customLabel.forEach((item: any) => {
        const value = groupInfo.customFields.find(customData => customData.code === item.code);
        if (value) {
          uniqueRow.push({
            title: item.label,
            content: value?.value?.replace(",", ", "),
          });
        }
      });
    }

    // The two microprogramming labels reach here as system labels (category
    // `group_configuration`, source EDH). They are pulled out of that list and
    // rendered as a dedicated pill instead, so the header shows the product
    // copy ("Programación aplicada") rather than the raw database name
    // ("Grupo con estructura de programación").
    const programmingState = resolveProgrammingGroupState(groupInfo?.labels);

    const title = (
      <Heading darkMode type="h1" className={styles.infoItem_title}>
        {`${groupInfo?.course?.code ? `${groupInfo?.course?.code}, ` : ""}${
          groupInfo?.course?.name
        }`}
        {groupInfo?.visibleForEnrollment ? (
          <Icon name="open-eye" size="lg" color="gray-10" className={styles.icon} />
        ) : (
          <Icon name="closed-eye" color="gray-10" size="lg" className={styles.icon} />
        )}
      </Heading>
    );

    const subTitle = () => {
      const capacity = groupInfo?.capacity ?? 0;
      const enrollmentsCount = groupInfo?.enrollmentStats?.enrollmentsCount
        ? `| ${groupInfo?.enrollmentStats?.enrollmentsCount}`
        : "";
      const waitingListsCount =
        groupInfo?.enrollmentStats?.waitingListsCount &&
        groupInfo?.enrollmentStats?.waitingListsCount !== 0
          ? ` (+${groupInfo?.enrollmentStats?.waitingListsCount})`
          : "";
      const idBundle = groupInfo?.links[0].bundle.id;

      const routeBundle = `/editor/vacancies/${workspace}/${scenario}/${origin}/${idBundle}`;
      return (
        <div className={groupStyles.subTitleContainer}>
          <div className={cx(styles.infoItem_subTitle, styles.infoItem_subTitleWrapper)}>
            <Pill color="gray-70" textColor="gray-10">
              {t("dimension.group.pill-section.campus")}:{" "}
              {groupInfo?.course?.curriculum?.program?.campus?.code}
            </Pill>
            <Pill color="gray-70" textColor="gray-10">
              {t("dimension.group.pill-section.period")}: {groupInfo?.term?.code}
            </Pill>
            {!!groupInfo?.clientCode && !!groupInfo?.code && (
              <Pill color="gray-70" textColor="gray-10">
                {`${t("dimension.group.header.code")}: ${groupInfo?.code}`}
              </Pill>
            )}
            <Pill color="gray-70" textColor="gray-10">
              {t("dimension.group.pill-section.vacancies")}:{" "}
              {`${capacity} ${enrollmentsCount} ${waitingListsCount}`}
            </Pill>
            {idBundle && (
              <LinkRouter
                size="sm"
                iconRight="external-link"
                to={routeBundle}
                className={cx(styles.link)}
              >
                {t("dimension.group.link")}: {groupInfo?.links[0].bundle.code}
              </LinkRouter>
            )}
          </div>

          {/* The row is also rendered for the proposals access alone, on a group without labels. */}
          {(systemLabels?.length > 0 || !!programmingState || !!proposalsAccess) && (
            <>
              <div className={cx(styles.infoItem_systemLabels, "systemLabelTour")}>
                {systemLabels.map(label => {
                  return <SystemLabel key={label?.id} label={label?.name} />;
                })}

                {!!programmingState && (
                  <Pill textColor="gray-80" color="gray-10">
                    {t(getProgrammingPillLabelKey(programmingState))}
                  </Pill>
                )}

                {!!proposalsAccess && (
                  <ProposalsAccess
                    hasIndicator={proposalsAccess.hasIndicator}
                    onOpen={proposalsAccess.onOpen}
                  />
                )}
              </div>

              {systemLabels?.length > 0 && showHotspot && (
                <TourTooltip
                  beaconClassName={groupStyles.tourBeacon}
                  startTour={showHotspot}
                  steps={steps}
                  onClose={() => setIsOnboardingOpen(false, "system-labels")}
                  onToolipOpen={() => setIsOnboardingOpen(true, "system-labels")}
                />
              )}
            </>
          )}
        </div>
      );
    };

    uniqueRow.length > 0 && details.push(uniqueRow);

    const labels = [];

    groupInfo?.labels?.forEach(label => {
      // Rendered by the dedicated programming pill above, with product copy.
      if (isProgrammingLabelCode(label?.code)) return;

      if (label?.category?.labelSource?.code !== "USER-DEFINED") {
        systemLabels.push(label);
      } else {
        labels.push(label);
      }
    });

    const hasRestrictionsSettings = baseState?.base?.restrictionConfigurations?.enabled;

    return {
      title: title,
      topTitle: groupInfo?.clientCode
        ? `${t("dimension.group.header.client-code-long")}: ${groupInfo?.clientCode}`
        : groupInfo?.code
          ? `${t("dimension.group.header.code")}: ${groupInfo?.code}`
          : "",
      subTitle: subTitle(),
      moreDetails: details || labels,
      children: (
        <>
          <div
            className={cx(
              groupStyles.labelsWrapper,
              context?.permits?.groupText &&
                !hasRestrictionsSettings &&
                groupStyles.labelsWrapper__bordered,
            )}
          >
            <Tags
              sectionTitle={t("header.tag-manual.title")}
              labels={labels}
              addMargin={details.length > 0}
              onEdit={onShowTagsDrawer}
            />
          </div>

          {hasRestrictionsSettings && (
            <div
              className={cx(
                groupStyles.labelsWrapper,
                context?.permits?.groupText && groupStyles.labelsWrapper__bordered,
              )}
            >
              <Restrictions onShowRestrictionsDrawer={onShowRestrictionsDrawer} />
            </div>
          )}

          {context?.permits?.groupText && (
            <div className={groupStyles.labelsWrapper}>
              <Reference onEdit={onShowReferenceDrawer} />
            </div>
          )}
        </>
      ),
    };
  };

  const header = useMemo(
    () => (state?.info?.info ? getHeaderInfo(state?.info?.info, state?.info?.customFields) : null),
    [state?.info?.info, state?.info?.customFields, language, calendarData, proposalsAccess],
  );

  useEffect(() => {
    if (calendarData === null && error === false) getDataSections();
  }, [calendarData, error, getDataSections]);

  useListenEvent({
    eventKey: "on:refresh",
    handler: (detail: Events["on:refresh"]) => {
      if (detail?.target === "group-calendar") {
        getDataSections();
      }
    },
  });

  return {
    header,
    loading,
    error,
    calendarData,
    groupInfo: state?.info?.info,
    setCalendar,
    setError,
    getDataSections,
  };
};

export default useGetHeaderInfo;
